import { canonicalUrl, getPublicRoute, isHeldRoute, SITE_ORIGIN } from '../../lib/analytics/publicRoutes';

import { FHA_VS_CONVENTIONAL_REVIEW, PROGRAM_REVIEW } from './constants';

const PROGRAM_PATHS = new Set([
  '/loans/conventional',
  '/loans/fha',
  '/loans/fha-vs-conventional',
  '/loans/non-qm',
  '/loans/bank-statement',
  '/loans/dscr'
]);

const SPECIALTY_PATHS = new Set([
  '/loans/non-qm',
  '/loans/bank-statement',
  '/loans/dscr'
]);

export const LOAN_PROGRAM_SCHEMA_ID = 'lw-loan-program-schema';

export function loanProgramStructuredDataGraph(pathname: string): object | null {
  if (!PROGRAM_PATHS.has(pathname)) return null;
  // A held path renders the 404 page; emitting an Article for it would describe
  // a document that is not there.
  if (isHeldRoute(pathname)) return null;
  const route = getPublicRoute(pathname);
  if (!route) return null;

  const pageUrl = canonicalUrl(route);
  const organizationId = `${SITE_ORIGIN}/#organization`;
  const webPageId = `${pageUrl}#webpage`;
  const articleId = `${pageUrl}#article`;
  const breadcrumbId = `${pageUrl}#breadcrumb`;
  const review = pathname === '/loans/fha-vs-conventional'
    ? FHA_VS_CONVENTIONAL_REVIEW
    : PROGRAM_REVIEW;
  const programName = pathname === '/loans/fha'
    ? 'FHA loans in California'
    : pathname === '/loans/conventional'
      ? 'Conventional loans in California'
      : pathname === '/loans/fha-vs-conventional'
        ? 'FHA and conventional loan comparison'
        : pathname === '/loans/non-qm'
          ? 'Non-QM mortgage programs and documentation paths'
          : pathname === '/loans/bank-statement'
            ? 'Bank Statement loans for self-employed borrowers'
            : 'DSCR loans for rental properties';
  const breadcrumbName = pathname === '/loans/fha'
    ? 'FHA loans'
    : pathname === '/loans/conventional'
      ? 'Conventional loans'
      : pathname === '/loans/fha-vs-conventional'
        ? 'FHA vs. conventional'
        : pathname === '/loans/non-qm'
          ? 'Non-QM loans'
          : pathname === '/loans/bank-statement'
            ? 'Bank Statement loans'
            : 'DSCR loans';
  const specialtyChild = pathname === '/loans/bank-statement' || pathname === '/loans/dscr';
  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
    { '@type': 'ListItem', position: 2, name: 'Loan programs', item: `${SITE_ORIGIN}/resources` },
    ...(specialtyChild
      ? [{ '@type': 'ListItem', position: 3, name: 'Non-QM loans', item: `${SITE_ORIGIN}/loans/non-qm` }]
      : []),
    { '@type': 'ListItem', position: specialtyChild ? 4 : 3, name: breadcrumbName, item: pageUrl }
  ];
  const organization = {
    '@type': 'Organization',
    '@id': organizationId,
    name: 'Lendwise Mortgage',
    legalName: 'Lendwise Mortgage Inc.',
    url: `${SITE_ORIGIN}/`,
    logo: `${SITE_ORIGIN}/assets/lendwise-logo-green-bg.jpg`,
    telephone: '+1-818-477-1073',
    identifier: {
      '@type': 'PropertyValue',
      propertyID: 'NMLS',
      value: '2702455'
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '21800 Oxnard Street, Suite 220',
      addressLocality: 'Woodland Hills',
      addressRegion: 'CA',
      postalCode: '91367',
      addressCountry: 'US'
    }
  };

  // These are evergreen program guides, not news or blog posts. Google frames
  // Article markup around news, blog, and sports articles, and recommends that
  // structured data represent the visible page precisely. The new specialty
  // cluster therefore uses WebPage + BreadcrumbList + Organization without an
  // invented byline date. The already-live legacy hubs retain their established
  // Article graph until their indexed markup is reviewed as a separate change.
  if (SPECIALTY_PATHS.has(pathname)) {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': webPageId,
          url: pageUrl,
          name: route.title,
          description: route.description,
          inLanguage: 'en-US',
          about: programName,
          publisher: { '@id': organizationId },
          breadcrumb: { '@id': breadcrumbId }
        },
        {
          '@type': 'BreadcrumbList',
          '@id': breadcrumbId,
          itemListElement: breadcrumbItems
        },
        organization
      ]
    };
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': articleId,
        headline: route.title.split(' | ')[0],
        description: route.description,
        url: pageUrl,
        mainEntityOfPage: { '@id': webPageId },
        datePublished: review.publishedOn,
        dateModified: review.reviewedOn,
        inLanguage: 'en-US',
        about: programName,
        author: { '@id': organizationId },
        publisher: { '@id': organizationId }
      },
      {
        '@type': 'WebPage',
        '@id': webPageId,
        url: pageUrl,
        name: route.title,
        description: route.description,
        inLanguage: 'en-US',
        reviewedBy: { '@id': organizationId },
        mainEntity: { '@id': articleId },
        breadcrumb: { '@id': breadcrumbId }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': breadcrumbId,
        itemListElement: breadcrumbItems
      },
      organization
    ]
  };
}
